/** Game constants: player colours, team identities and unit naming. Part of src/constants.ts (re-exported there; import from '../constants'). */
import type { PlayerColor, TeamPalette } from '../types';

// =============================================================================
// PLAYER COLORS — soft Astroneer pastels
// =============================================================================

export const PLAYER_COLORS: PlayerColor[] = [
  { hex: '#7eb8d4', r: 0.49, g: 0.72, b: 0.83 },
  { hex: '#d4846a', r: 0.83, g: 0.52, b: 0.42 },
  { hex: '#88c4a0', r: 0.53, g: 0.77, b: 0.63 },
  { hex: '#d4c078', r: 0.83, g: 0.75, b: 0.47 },
];

// =============================================================================
// TEAMS — company identities with color schemes
// =============================================================================

export const TEAMS: TeamPalette[] = [
  {
    name: 'Sidereal Conservancy',
    tagline: 'Protecting the stars, one system at a time.',
    primary: '#6e9ec2',
    accent: '#4a7494',
    visor: '#a8d4e8',
    detail: '#8bb8d4',
    helmet: 'dome',
    visorStyle: 'band',
  },
  {
    name: 'Crater & Bloom',
    tagline: 'We reshape worlds. Professionally.',
    primary: '#6a9a72',
    accent: '#4e7856',
    visor: '#c8dca0',
    detail: '#8ab890',
    helmet: 'pods',
    visorStyle: 'goggles',
  },
  {
    name: 'TerraGently Inc.',
    tagline: 'Sustainability is our bottom line.',
    primary: '#a8808c',
    accent: '#846470',
    visor: '#d4b8c0',
    detail: '#c098a4',
    helmet: 'cap',
    visorStyle: 'slit',
  },
  {
    name: 'GreenOrbit Solutions',
    tagline: 'Optimizing ecological outcomes since 2847.',
    primary: '#a89878',
    accent: '#847860',
    visor: '#d0c8a8',
    detail: '#bcac8c',
    helmet: 'hardhat',
    visorStyle: 'band',
  },
  {
    name: 'BioSphere Direct',
    tagline: 'Conservation. Delivered.',
    primary: '#c4845c',
    accent: '#9a6640',
    visor: '#e8c8a0',
    detail: '#d4a078',
    helmet: 'headset',
    visorStyle: 'bubble',
  },
  {
    name: 'AstraTurf Co.',
    tagline: 'If it\'s not green, we\'ll make it green.',
    primary: '#c4b05c',
    accent: '#9a8a3e',
    visor: '#e8dca0',
    detail: '#d4c878',
    helmet: 'lampbar',
    visorStyle: 'goggles',
  },
  {
    name: 'Gentle Futures Group',
    tagline: 'Investing in tomorrow\'s biomes today.',
    primary: '#9478a8',
    accent: '#705c84',
    visor: '#c8b8d8',
    detail: '#ac94c0',
    helmet: 'crest',
    visorStyle: 'slit',
  },
  {
    name: 'PlanetCare Ltd.',
    tagline: 'Reliable planetary stewardship.',
    primary: '#868a96',
    accent: '#666a76',
    visor: '#b4b8c4',
    detail: '#9ca0ac',
    helmet: 'dome',
    visorStyle: 'bubble',
  },
  {
    name: 'Orbital Reclamation Bureau',
    tagline: 'Authorized by the GCPPA. Mostly.',
    // teal (ruling 23): was a second green, 15 apart from Crater & Bloom under the unit palette; now 47 from its nearest team
    primary: '#5c8a86',
    accent: '#406864',
    visor: '#98c0bc',
    detail: '#74a4a0',
    helmet: 'pods',
    visorStyle: 'band',
  },
  {
    name: 'EcoShield LLC',
    tagline: 'Aggressive environmental defense.',
    primary: '#b86e6e',
    accent: '#8c4e4e',
    visor: '#d8a8a0',
    detail: '#c88c84',
    helmet: 'flaps',
    visorStyle: 'slit',
  },
];

// Legacy company names (for backwards compatibility)
export const COMPANY_NAMES = TEAMS.map(t => t.name);

// =============================================================================
// TEAMS — UNIT NAMING
// =============================================================================

export const UNIT_SUFFIXES = ['Alpha', 'Bravo', 'Charlie'];
export const DEFAULT_FRIENDLY_FIRE = 0;
